import { Button, Tooltip } from '@dixa/lib-shared-habla';
import { useHasAccess } from 'lib-shared-access-control';
import { ampli } from 'lib-shared-tracking';
import { shallowEqual } from 'react-redux';

import { CommandKeybindingsTooltipContent } from 'components/commandKeybindingsTooltipContent';
import { actions } from 'components/conversation/actions/conversation';
import { getLatestFacebookConversation } from 'components/conversation/selectors';
import { canClaimConversation } from 'components/conversation/utils/canClaimConversation';
import { canTakeOverConversation } from 'components/conversation/utils/canTakeOverConversation';
import { useCommandHandler } from 'utils/commands';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { bindActionToPromise } from 'utils/sagaApiGeneratorHelpers';

const { conversationClaim } = actions;

export const ConversationClaimButton = (): JSX.Element | null => {
  const dispatch = useAppDispatch();
  const hasClaimAccess = useHasAccess('conversation.claim');
  const { conversation, isAssignedToMe, latestFacebookCsid, asyncStatus } =
    useAppSelector(
      (state) => ({
        conversation: state.conversation.metaConversation.conversation,
        isAssignedToMe: state.conversation.metaConversation.isAssignedToMe,
        latestFacebookCsid: getLatestFacebookConversation(state),
        asyncStatus: state.conversation.asyncStatus,
      }),
      shallowEqual,
    );

  const canTakeOver = canTakeOverConversation(conversation, isAssignedToMe);
  const canClaim = canClaimConversation(
    conversation,
    isAssignedToMe,
    latestFacebookCsid,
  );

  const isDisabled = !canClaim && !canTakeOver;

  useCommandHandler({
    name: 'conversation:claim',
    actionFn: () => {
      if (!hasClaimAccess || isDisabled) {
        return;
      }

      claimConversation();
    },
  });

  const claimConversation = async () => {
    await bindActionToPromise(
      dispatch,
      conversationClaim,
    )({
      csid: conversation.id,
    });

    ampli.conversationClaimed({
      conversationId: conversation.id ?? 0,
      location: 'conversation',
      type: canClaim ? 'claim' : 'takeOver',
      channel: conversation?.initial_channel || undefined,
      language: conversation?.attributes?.language,
      tags: conversation.tags,
    });
  };

  const titles = canTakeOver
    ? {
        loading: 'Taking over...',
        button: 'Take over conversation',
        tooltip: "You don't have permission to take over conversation",
      }
    : {
        loading: 'Claiming...',
        button: 'Claim conversation',
        tooltip: "You don't have permission to claim conversation",
      };

  const tooltipContent = (
    <CommandKeybindingsTooltipContent commandName="conversation:claim">
      {titles.button}
    </CommandKeybindingsTooltipContent>
  );

  return (
    <Tooltip
      disabled={asyncStatus.claim === 'loading' || isDisabled}
      content={hasClaimAccess ? tooltipContent : titles.tooltip}
    >
      <Button
        variant="primary"
        isLoading={asyncStatus.claim === 'loading'}
        loadingIndicator={titles.loading}
        onClick={claimConversation}
        disabled={!hasClaimAccess || isDisabled}
      >
        {titles.button}
      </Button>
    </Tooltip>
  );
};
